import { useEffect, useState } from "react";
import { AlertCircle, CheckCircle2, PackagePlus } from "lucide-react";
import {
  AddOnChangeDialog, AddOnQuantityControl, Button, Card, CardContent, CardHeader, CardTitle,
  cn, localeText, type AddOnChangePreviewData,
} from "@wa/ui";
import { ApiError } from "../../../lib/api";
import { fmtMoney } from "../../../lib/format";
import { billing, type AddOnsOverview, type BillingAddOn } from "../../../lib/billing";

type T = (k: string, v?: Record<string, string | number>) => string;

/** Fallback when the API sends no `maxQuantity` - mirrors DEFAULT_ADD_ON_MAX_QUANTITY. */
const DEFAULT_ADD_ON_MAX = 50;

/**
 * Self-service add-ons - buy / change quantity / remove. Renders only when the catalog has
 * add-ons. An add-on is a recurring line on the account's Stripe subscription (the same
 * invoice as the plan).
 */
export function AddOnsSection({
  billingSiteId, locale, t, onChanged,
}: {
  billingSiteId: string | null;
  locale: string;
  t: T;
  onChanged: () => void;
}) {
  const [data, setData] = useState<AddOnsOverview | null>(null);
  const [pending, setPending] = useState<Record<string, number>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<{ code: string; name: string; qty: number; decreasing: boolean; price: string } | null>(null);
  // Impact preview: what this quantity takes away + whether a site choice is mandatory.
  // Loaded when the dialog opens, because it hits the DB for usage (storage, AI quotas).
  const [changePreview, setChangePreview] = useState<AddOnChangePreviewData | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [keepIds, setKeepIds] = useState<string[]>([]);
  const [notice, setNotice] = useState<{ kind: "success" | "error"; key: string } | null>(null);

  function seed(d: AddOnsOverview) {
    setData(d);
    // Clamp to the add-on ceiling: an account that managed to buy 2 x a switch-type add-on
    // gets a pending quantity of 1, so the row offers the correction (and the second,
    // useless line leaves the invoice) instead of the switch hiding it.
    setPending(Object.fromEntries(d.addOns.map((a) => [a.code, Math.min(a.quantity, a.maxQuantity ?? DEFAULT_ADD_ON_MAX)])));
  }

  useEffect(() => {
    if (!billingSiteId) return;
    let alive = true;
    billing.addOns(billingSiteId).then((d) => alive && seed(d)).catch(() => alive && setData(null));
    return () => { alive = false; };
  }, [billingSiteId]);

  if (!data || data.addOns.length === 0) return null;

  const loc = locale === "et" ? "et" : "en";
  const cycle = data.billingCycle;
  const perLabel = cycle === "yearly" ? t("billing.perYear") : t("billing.perMonth");
  const priceCentsFor = (a: BillingAddOn) => (cycle === "yearly" ? a.priceYearlyCents : a.priceMonthlyCents);
  const priceLabel = (a: BillingAddOn, qty: number) =>
    `${fmtMoney(priceCentsFor(a) * Math.max(1, qty), a.currency, locale)}/${perLabel}`;

  /** Open the confirm dialog + load the impact preview (what is lost, is a choice needed). */
  function openConfirm(a: BillingAddOn, qty: number) {
    if (!billingSiteId) return;
    setConfirm({ code: a.code, name: localeText(a.name, loc), qty, decreasing: qty < a.quantity, price: priceLabel(a, qty) });
    setChangePreview(null);
    setKeepIds([]);
    setPreviewLoading(true);
    billing
      .addOnPreview(billingSiteId, a.code, qty)
      .then(setChangePreview)
      // A failed preview must not block the change - the server enforces the quota anyway
      // (409 `sites_over_quota`) and that surfaces as an error notice.
      .catch(() => setChangePreview(null))
      .finally(() => setPreviewLoading(false));
  }

  function closeConfirm() {
    setConfirm(null);
    setChangePreview(null);
    setKeepIds([]);
  }

  async function apply(code: string, qty: number, keep: string[]) {
    if (!billingSiteId) return;
    setBusy(code);
    setNotice(null);
    try {
      const res = await billing.setAddOn(billingSiteId, code, qty, keep.length > 0 ? keep : undefined);
      setNotice({
        kind: "success",
        key: res.change === "removed" ? "billing.addOns.removed" : res.change === "added" ? "billing.addOns.added" : "billing.addOns.updated",
      });
      const d = await billing.addOns(billingSiteId);
      seed(d);
      onChanged(); // the plan card's entitlements changed -> refresh the parent
    } catch (e) {
      // The server refuses a quota breach without a site choice - show that separately, or
      // the user cannot tell why "save" does nothing.
      const code409 = e instanceof ApiError && typeof e.body === "object" && e.body && "error" in e.body
        ? String((e.body as { error: unknown }).error)
        : "";
      setNotice({ kind: "error", key: code409 === "sites_over_quota" ? "billing.addOns.quotaError" : "billing.addOns.error" });
    } finally {
      setBusy(null);
      closeConfirm();
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <PackagePlus className="size-4 text-muted-foreground" />
          <CardTitle className="text-base">{t("billing.addOns.title")}</CardTitle>
        </div>
        <p className="mt-0.5 text-sm text-muted-foreground">{t("billing.addOns.subtitle")}</p>
      </CardHeader>
      <CardContent className="space-y-3">
        {notice && (
          <div
            className={cn(
              "flex items-center gap-2 rounded-lg border p-2.5 text-sm",
              notice.kind === "success" ? "border-success/30 bg-success/10 text-success" : "border-destructive/30 bg-destructive/10 text-destructive",
            )}
          >
            {notice.kind === "success" ? <CheckCircle2 className="size-4 shrink-0" /> : <AlertCircle className="size-4 shrink-0" />}
            {t(notice.key)}
          </div>
        )}
        {!data.canPurchase && (
          <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/40 p-2.5 text-sm text-muted-foreground">
            <AlertCircle className="mt-0.5 size-4 shrink-0" />
            {t("billing.addOns.needPaidPlan")}
          </div>
        )}
        <ul className="divide-y divide-border">
          {data.addOns.map((a) => {
            const cur = a.quantity;
            const pend = pending[a.code] ?? cur;
            const changed = pend !== cur;
            const disabled = !data.canPurchase || !a.synced || busy != null;
            const max = a.maxQuantity ?? DEFAULT_ADD_ON_MAX;
            const single = max <= 1; // switch-style add-on: one per account
            const name = localeText(a.name, loc);
            return (
              <li key={a.code} className="flex flex-wrap items-center justify-between gap-3 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground">{name}</p>
                  <p className="text-xs text-muted-foreground">
                    {priceLabel(a, 1)}
                    {cur > 0 && (
                      <span className="text-success"> · {single ? t("billing.addOns.enabled") : t("billing.addOns.current", { n: cur })}</span>
                    )}
                    {!a.synced && <span> · {t("billing.addOns.unavailable")}</span>}
                  </p>
                  {single && <p className="text-xs text-muted-foreground">{t("billing.addOns.singleHint")}</p>}
                </div>
                <div className="flex items-center gap-2">
                  <AddOnQuantityControl
                    value={pend}
                    max={max}
                    disabled={disabled}
                    switchLabel={name}
                    onChange={(next) => setPending((p) => ({ ...p, [a.code]: next }))}
                  />
                  {changed && (
                    <Button
                      size="sm"
                      variant={pend === 0 ? "outline" : "primary"}
                      loading={busy === a.code}
                      onClick={() => openConfirm(a, pend)}
                    >
                      {pend === 0 ? t("billing.addOns.remove") : t("billing.addOns.save")}
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
        <p className="text-xs text-muted-foreground">{t("billing.addOns.hint")}</p>
      </CardContent>

      <AddOnChangeDialog
        open={confirm != null}
        addOnName={confirm?.name ?? ""}
        quantity={confirm?.qty ?? 0}
        priceLabel={confirm?.price ?? ""}
        decreasing={confirm?.decreasing ?? false}
        preview={changePreview}
        loading={previewLoading}
        applying={busy != null}
        keepIds={keepIds}
        onKeepChange={setKeepIds}
        locale={loc}
        onConfirm={() => confirm && apply(confirm.code, confirm.qty, keepIds)}
        onCancel={closeConfirm}
      />
    </Card>
  );
}
